import { addMessageAdvanced } from "~/redux/globalMessages/actions";
import { CART_PNA_THRESHOLD } from "~/utils/constants";
import { selectLineItemsByProduct } from "~/Cart/redux/selectors/cartSelectors";
import { orchestratePnARefresh } from "~/Cart/redux/actions/cartRefresh";
import { cartLoadErrorAction, cartLoadFinishAction, cartLoadStartAction } from "~/Cart/pages/CartPage/redux/actions";
import { getData } from "~/Cart/api/CartView";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "~/redux/store";
import { type AnyAction } from "~/redux/types";
import { userIsCustomer } from "~/utils/roles";
import { type TransformedCartData } from "~/Cart/transformers/CartViewDataTransformer";
import { trackCartContents } from "~/feature/Analytics/ga/abandonment";
import { TranslationType } from "~/common/TranslationModel";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type AsyncThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, AnyAction>;
function showAboveThresholdMessageAction() {
  return addMessageAdvanced("info", {
    value: "page.cart.message.abovePnaThreshold",
    type: TranslationType.INTERNAL,
    params: { count: CART_PNA_THRESHOLD },
  });
}

function showBulkUploadSuccessMessageAction() {
  const params = new URLSearchParams(window.location.search);
  const count = +params.get("bulkSuccess")!;
  return addMessageAdvanced("info", {
    value: "cartPage.msg.successfullyAdded",
    type: TranslationType.INTERNAL,
    params: { count },
  });
}

function loadCartDataThunk(): AsyncThunkResult<TransformedCartData> {
  return async (dispatch) => {
    dispatch(cartLoadStartAction());

    try {
      const data = await getData();
      dispatch(cartLoadFinishAction(data));
      return data;
    } catch (e) {
      console.error(e);
      dispatch(cartLoadErrorAction());
      throw e;
    }
  };
}

export function cartPageLoadThunk(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const data = await dispatch(loadCartDataThunk());

    if (userIsCustomer()) {
      const params = new URLSearchParams(window.location.search);
      if (params.has("bulkSuccess")) {
        dispatch(showBulkUploadSuccessMessageAction());
      }

      if (Object.values(data.cartEntriesById).length <= CART_PNA_THRESHOLD) {
        dispatch(performCartRefresh());
      } else {
        dispatch(showAboveThresholdMessageAction());
      }

      // This is disabled while legal approved texts are not available
      // When they are provided, this can be uncommented
      // Replace the lorem ipsum below with the corresponding translation key

      // const hasConfiguratorProduct = Object.values(getState().products.basic).some((product) =>
      //   isConfiguratorProduct(product),
      // );
      // if (hasConfiguratorProduct) {
      //   dispatch(addMessage("info", "Lorem ipsum - LC Configuration present in cart"));
      // }

      trackCartContents(getState());
    }
  };
}

function performCartRefresh(): ThunkResult<void> {
  return (dispatch, getState) => {
    const lineItemsByProduct = selectLineItemsByProduct(getState());
    const entries = Object.values(lineItemsByProduct);
    entries.sort((a, b) => a[0].cartEntryNumber - b[0].cartEntryNumber);

    entries.forEach((lineItems) => dispatch(orchestratePnARefresh(lineItems)));
  };
}
